/* Real auth gate: backed by /api/auth/*.
 *
 * Visual design borrows from auth.jsx (the demo flow), but every action
 * here hits a real endpoint. The mock AuthFlow is still toggle-able from
 * the tweaks panel for showing the design.
 */
import React from 'react';
import {
  login as apiLogin, register as apiRegister, getMe, getInvitation,
  forgotPassword, checkPasswordReset, resetPassword,
} from '../api.js';

function readInviteTokenFromUrl() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('invite') || '';
}

function readResetTokenFromUrl() {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('reset') || '';
}

export const AuthGate = ({ children }) => {
  const [user, setUser] = React.useState(undefined); // undefined = loading
  const inviteToken = readInviteTokenFromUrl();
  const resetToken = readResetTokenFromUrl();
  const [mode, setMode] = React.useState(resetToken ? 'reset' : inviteToken ? 'register' : 'login');

  React.useEffect(() => {
    getMe().then(u => setUser(u || null)).catch(() => setUser(null));
  }, []);

  // Once authed via the reset form, drop the spent ?reset= token from the
  // URL so a reload doesn't land on a dead link.
  const onAuthedFromReset = u => {
    try { window.history.replaceState(null, '', window.location.pathname + window.location.hash); } catch { /* ignore */ }
    setUser(u);
  };

  if (user === undefined) {
    return (
      <div style={{position:'fixed',inset:0,display:'flex',alignItems:'center',justifyContent:'center',background:'var(--bg)'}}>
        <div style={{color:'var(--fg-muted)',fontSize:14}}>Loading…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <AuthShell>
        {mode === 'reset'
          ? <ResetPasswordForm token={resetToken} onSwitch={() => setMode('login')} onAuthed={onAuthedFromReset}/>
          : mode === 'forgot'
          ? <ForgotPasswordForm onSwitch={() => setMode('login')}/>
          : mode === 'login'
          ? <LoginForm onSwitch={() => setMode('register')} onForgot={() => setMode('forgot')} onAuthed={setUser}/>
          : <RegisterForm onSwitch={() => setMode('login')} onAuthed={setUser} inviteToken={inviteToken}/>}
      </AuthShell>
    );
  }

  return children(user, () => setUser(null));
};

const AuthShell = ({ children }) => (
  <div style={{position:'fixed',inset:0,background:'var(--eve-gradient)',zIndex:200,overflow:'auto',display:'flex',flexDirection:'column'}}>
    <div className="auth-shell__head">
      <img
        src="/assets/eraneos-wordmark.svg"
        alt="Eraneos"
        style={{height:30,width:'auto',display:'block'}}
      />
      <div style={{width:1,height:22,background:'rgba(26,20,20,0.25)'}}/>
      <div style={{fontSize:13,letterSpacing:'0.04em',color:'#3A2C28',fontWeight:700}}>eOS</div>
    </div>
    <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px 20px 60px'}}>
      <div className="auth-grid">
        <div className="auth-hide-on-mobile">
          <div style={{fontSize:11,letterSpacing:'0.18em',color:'#3A2C28',textTransform:'uppercase',marginBottom:12,fontWeight:600}}>The firm, as a system</div>
          <div style={{fontFamily:'var(--font-display)',fontSize:'clamp(36px, 6vw, 64px)',lineHeight:0.95,letterSpacing:'-0.02em',color:'#1A1414'}}>The agentic organisation.</div>
          <div style={{fontSize:18,color:'#3A2C28',marginTop:18,maxWidth:480,lineHeight:1.45}}>
            One workspace for every consultant. Every chat, every agent run, every artifact — saved into the firm's own repo, learning your craft as it goes.
          </div>
          <div style={{display:'flex',gap:18,marginTop:32,fontSize:13,color:'#3A2C28',flexWrap:'wrap'}}>
            <span>● Memory per person</span>
            <span>● Function MD files</span>
            <span>● Digital twins</span>
            <span>● OrgRepo</span>
          </div>
        </div>
        {children}
      </div>
    </div>
  </div>
);

const Card = ({ children }) => (
  <div style={{padding:36,background:'rgba(255,255,255,0.78)',backdropFilter:'blur(20px)',borderRadius:16,border:'1px solid rgba(0,0,0,0.06)'}}>
    {children}
  </div>
);

const LoginForm = ({ onSwitch, onForgot, onAuthed }) => {
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const submit = async e => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const user = await apiLogin({ email, password });
      onAuthed(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <form onSubmit={submit}>
        <div style={{fontFamily:'var(--font-display)',fontSize:28,marginBottom:8}}>Sign in</div>
        <div style={{fontSize:13,color:'var(--fg-muted)',marginBottom:24}}>Use your firm email and password.</div>
        <div className="field">
          <label>Email</label>
          <input className="input" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)}/>
        </div>
        <div className="field">
          <label>Password</label>
          <input className="input" type="password" autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)}/>
        </div>
        {error && <ErrorBox>{error}</ErrorBox>}
        <button type="submit" className="btn-orange" style={{width:'100%',padding:'10px 16px',fontSize:14,marginTop:8}} disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in →'}
        </button>
        <div style={{display:'flex',justifyContent:'space-between',marginTop:14,fontSize:12.5}}>
          <a style={{color:'var(--fg-muted)',cursor:'pointer'}} onClick={onForgot}>Forgot password?</a>
          <a style={{color:'var(--orange)',cursor:'pointer'}} onClick={onSwitch}>Create an account →</a>
        </div>
      </form>
    </Card>
  );
};

const RegisterForm = ({ onSwitch, onAuthed, inviteToken }) => {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  // Invite lookup: undefined = pending, null = no invite or lookup failed, object = valid invite metadata
  const [invite, setInvite] = React.useState(inviteToken ? undefined : null);
  const [inviteError, setInviteError] = React.useState(null);

  React.useEffect(() => {
    if (!inviteToken) return;
    let cancelled = false;
    getInvitation(inviteToken)
      .then(inv => {
        if (cancelled) return;
        setInvite(inv);
        if (inv.email) setEmail(inv.email);
      })
      .catch(err => {
        if (cancelled) return;
        setInvite(null);
        setInviteError(err.message);
      });
    return () => { cancelled = true; };
  }, [inviteToken]);

  const submit = async e => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const { user } = await apiRegister({ email, password, name, inviteToken: inviteToken || undefined });
      onAuthed(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (invite === undefined) {
    return <Card><div style={{padding:20,color:'var(--fg-muted)',fontSize:14}}>Checking invitation…</div></Card>;
  }

  return (
    <Card>
      <form onSubmit={submit}>
        <div style={{fontFamily:'var(--font-display)',fontSize:28,marginBottom:8}}>Create your account</div>
        {invite ? (
          <div style={{fontSize:'var(--text-sm)',color:'var(--fg-muted)',marginBottom:'var(--space-6)',padding:'var(--space-3)',background:'#FFF3EE',borderRadius:'var(--r-md)',border:'1px solid #FFD7C6'}}>
            <strong style={{color:'var(--fg)'}}>{invite.invitedByName}</strong> invited you to join
            {invite.functionName ? <> as <strong style={{color:'var(--fg)'}}>{invite.functionName}</strong></> : <> as <strong style={{color:'var(--fg)'}}>{invite.role}</strong></>}.
            {invite.note && <div style={{marginTop:'var(--space-2)',fontStyle:'italic'}}>"{invite.note}"</div>}
          </div>
        ) : inviteToken ? (
          <div style={{fontSize:13,color:'#A20235',marginBottom:24,padding:12,background:'#FCE6E0',borderRadius:8}}>
            This invitation isn't valid anymore{inviteError ? ` (${inviteError})` : ''}. Ask the person who invited you for a fresh link.
          </div>
        ) : (
          <div style={{fontSize:13,color:'var(--fg-muted)',marginBottom:24}}>
            Registration is invite-only. If this is the first account, it will be created as the firm's admin — otherwise ask an admin to send you a link.
          </div>
        )}
        <div className="field"><label>Full name</label><input className="input" required value={name} onChange={e => setName(e.target.value)}/></div>
        <div className="field"><label>Email</label><input className="input" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} readOnly={!!(invite && invite.email)}/></div>
        <div className="field"><label>Password</label><input className="input" type="password" autoComplete="new-password" required minLength={12} placeholder="At least 12 characters" value={password} onChange={e => setPassword(e.target.value)}/></div>
        {invite && invite.functionName && (
          <div className="field">
            <label>Role / function</label>
            <input
              className="input"
              value={invite.functionName}
              readOnly
              tabIndex={-1}
              style={{background:'var(--bg-alt)',cursor:'not-allowed'}}
            />
            <div style={{fontSize:'var(--text-xs)',color:'var(--fg-muted)',marginTop:'var(--space-1)'}}>
              Set by your inviter. Ask an admin if you need to change roles after signing up.
            </div>
          </div>
        )}
        {error && <ErrorBox>{error}</ErrorBox>}
        <button type="submit" className="btn-orange" style={{width:'100%',padding:'10px 16px',fontSize:14,marginTop:8}} disabled={busy || (!!inviteToken && !invite)}>
          {busy ? 'Creating…' : 'Create account →'}
        </button>
        <div style={{textAlign:'right',marginTop:14,fontSize:12.5}}>
          <a style={{color:'var(--orange)',cursor:'pointer'}} onClick={onSwitch}>I already have an account →</a>
        </div>
      </form>
    </Card>
  );
};

/* "Forgot password?" — collects the email, POSTs /api/auth/forgot and shows
 * a non-revealing confirmation. The endpoint never says whether the email
 * has an account; `delivery: 'none'` only means no email provider is
 * configured, in which case the way out is an admin-issued reset link. */
const ForgotPasswordForm = ({ onSwitch }) => {
  const [email, setEmail] = React.useState('');
  const [sent, setSent] = React.useState(null); // null | { delivery }
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const submit = async e => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await forgotPassword(email);
      setSent(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      {sent ? (
        <div>
          <div style={{fontFamily:'var(--font-display)',fontSize:28,marginBottom:8}}>
            {sent.delivery === 'email' ? 'Check your inbox' : 'Ask an admin'}
          </div>
          <div style={{fontSize:13.5,color:'var(--fg-muted)',lineHeight:1.6,marginBottom:20}}>
            {sent.delivery === 'email' ? (
              <>If <strong style={{color:'var(--fg)'}}>{email}</strong> has an account, a reset link is on its way.
              It works once and expires in 1 hour. Don't forget the spam folder.</>
            ) : (
              <>Email delivery isn't configured on this installation yet. Ask an admin —
              they can generate a one-time reset link for you from your profile's Settings tab
              and share it with you directly.</>
            )}
          </div>
          <a style={{color:'var(--orange)',cursor:'pointer',fontSize:12.5}} onClick={onSwitch}>← Back to sign in</a>
        </div>
      ) : (
        <form onSubmit={submit}>
          <div style={{fontFamily:'var(--font-display)',fontSize:28,marginBottom:8}}>Reset your password</div>
          <div style={{fontSize:13,color:'var(--fg-muted)',marginBottom:24}}>
            Enter your firm email and we'll send you a one-time link to set a new password.
          </div>
          <div className="field">
            <label>Email</label>
            <input className="input" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)}/>
          </div>
          {error && <ErrorBox>{error}</ErrorBox>}
          <button type="submit" className="btn-orange" style={{width:'100%',padding:'10px 16px',fontSize:14,marginTop:8}} disabled={busy}>
            {busy ? 'Sending…' : 'Send reset link →'}
          </button>
          <div style={{textAlign:'right',marginTop:14,fontSize:12.5}}>
            <a style={{color:'var(--orange)',cursor:'pointer'}} onClick={onSwitch}>← Back to sign in</a>
          </div>
        </form>
      )}
    </Card>
  );
};

/* The landing form for /?reset=<token> links (emailed or admin-shared).
 * Validates the token first so a dead link fails fast with the reason,
 * then sets the new password and signs the person straight in. */
const ResetPasswordForm = ({ token, onSwitch, onAuthed }) => {
  const [check, setCheck] = React.useState(undefined); // undefined = probing, { valid, email?, reason? }
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [error, setError] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    checkPasswordReset(token)
      .then(res => { if (!cancelled) setCheck(res); })
      .catch(err => { if (!cancelled) setCheck({ valid: false, reason: err.message }); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async e => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) { setError('The two passwords don\'t match.'); return; }
    setBusy(true);
    try {
      const user = await resetPassword({ token, password });
      onAuthed(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (check === undefined) {
    return <Card><div style={{padding:20,color:'var(--fg-muted)',fontSize:14}}>Checking reset link…</div></Card>;
  }

  if (!check.valid) {
    return (
      <Card>
        <div style={{fontFamily:'var(--font-display)',fontSize:28,marginBottom:8}}>Link no longer valid</div>
        <div style={{fontSize:13,color:'#A20235',marginBottom:20,padding:12,background:'#FCE6E0',borderRadius:8}}>
          This reset link can't be used{check.reason ? ` — ${check.reason}` : ''}. Request a fresh one
          from "Forgot password?" on the sign-in page, or ask an admin.
        </div>
        <a style={{color:'var(--orange)',cursor:'pointer',fontSize:12.5}} onClick={onSwitch}>← Back to sign in</a>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={submit}>
        <div style={{fontFamily:'var(--font-display)',fontSize:28,marginBottom:8}}>Choose a new password</div>
        <div style={{fontSize:13,color:'var(--fg-muted)',marginBottom:24}}>
          Setting a new password for <strong style={{color:'var(--fg)'}}>{check.email}</strong>. You'll be signed in right after.
        </div>
        <div className="field">
          <label>New password</label>
          <input className="input" type="password" autoComplete="new-password" required minLength={12} placeholder="At least 12 characters" value={password} onChange={e => setPassword(e.target.value)}/>
        </div>
        <div className="field">
          <label>Repeat password</label>
          <input className="input" type="password" autoComplete="new-password" required minLength={12} value={confirm} onChange={e => setConfirm(e.target.value)}/>
        </div>
        {error && <ErrorBox>{error}</ErrorBox>}
        <button type="submit" className="btn-orange" style={{width:'100%',padding:'10px 16px',fontSize:14,marginTop:8}} disabled={busy}>
          {busy ? 'Saving…' : 'Set password & sign in →'}
        </button>
      </form>
    </Card>
  );
};

const ErrorBox = ({ children }) => (
  <div style={{marginTop:8,padding:'10px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:8,fontSize:13}}>
    {children}
  </div>
);
